<template>
	<div
		v-if="open"
		class="fixed inset-0 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
		style="z-index: 10000"
		@click.self="$emit('cancel')">
		<div class="w-full max-w-lg rounded-2xl bg-white dark:bg-dark-700 p-5 shadow-2xl">
			<!-- Header -->
			<div class="flex items-center justify-between mb-3">
				<h3 class="text-base font-semibold text-gray-900 dark:text-white">Edit image</h3>
				<button
					type="button"
					class="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-white"
					@click="$emit('cancel')">
					✕
				</button>
			</div>

			<!-- Cropper area (needs a fixed height) -->
			<div class="h-80 rounded-lg overflow-hidden bg-black">
				<img ref="img" :src="src" class="block max-w-full" @load="initCropper" />
			</div>

			<!-- Controls -->
            <div class="flex items-center justify-center">
                <div class="mt-4 space-y-3 text-gray-600 dark:text-gray-300 w-[20rem]">
                    <div class="flex items-center gap-3">
                        <span class="w-5 text-center">−</span>
                        <input
                            type="range" min="1" max="3" step="0.01"
                            :value="zoom"
                            class="flex-1 accent-primary-400"
                            @input="onZoom" />
                        <span class="w-5 text-center">+</span>
                    </div>

                    <div class="flex items-center gap-3">
                        <button type="button" class="w-5 text-center" title="Rotate left 90°" @click="rotateBy(-90)">↺</button>
                        <input
                            type="range" min="-180" max="180" step="1"
                            :value="rotation"
                            class="flex-1 accent-primary-400"
                            @input="onRotate" />
                        <button type="button" class="w-5 text-center" title="Rotate right 90°" @click="rotateBy(90)">↻</button>
                    </div>
                    <p class="text-xs text-center opacity-60">
                        Zoom {{ zoom.toFixed(2) }}× · Rotation {{ rotation }}°
                    </p>
                </div>
            </div>
			

			<!-- Actions -->
			<div class="mt-5 flex justify-end gap-2">
				<button
					type="button"
					class="px-4 py-2 rounded-lg border text-sm border-gray-300 dark:border-dark-500 text-gray-700 dark:text-gray-200"
					@click="$emit('cancel')">
					Cancel
				</button>
				<button
					type="button"
					class="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-primary-400 hover:bg-primary-600"
					@click="confirm">
					OK
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

export default {
	name: "ImageCropperModal",
	props: {
		open: Boolean,
		src: String,
		fileName: { type: String, default: "image.jpg" },
		aspect: { type: Number, default: NaN }, // NaN = free crop, or e.g. 16 / 9, 4 / 5
	},
	emits: ["cancel", "done"],
	data() {
		return { zoom: 1, rotation: 0 };
	},
	created() {
		this.cropper = null; // deliberately NOT in data(): Cropper must not be made reactive
	},
	watch: {
		open(val) {
			if (!val) this.destroyCropper();
		},
	},
	beforeUnmount() {
		this.destroyCropper();
	},
	methods: {
		initCropper() {
			this.destroyCropper();
			this.zoom = 1;
			this.rotation = 0;
			this.cropper = new Cropper(this.$refs.img, {
				viewMode: 1,
				dragMode: "move", // image moves under a fixed frame
				aspectRatio: this.aspect,
				autoCropArea: 1,
				cropBoxMovable: false,
				cropBoxResizable: false,
				toggleDragModeOnDblclick: false,
				background: false,
				zoom: (e) => {
					// keep slider in sync with wheel/pinch zoom, and clamp to the slider range
					const next = this.zoom * (e.detail.ratio / e.detail.oldRatio);
					if (next < 0.99 || next > 3.01) return e.preventDefault();
					this.zoom = Math.min(3, Math.max(1, next));
				},
			});
		},
		destroyCropper() {
			if (this.cropper) {
				this.cropper.destroy();
				this.cropper = null;
			}
		},
		onZoom(e) {
			if (!this.cropper) return;
			const target = Number(e.target.value);
			this.cropper.zoom(target / this.zoom - 1); // relative zoom; the zoom event updates this.zoom
		},
		onRotate(e) {
			if (!this.cropper) return;
			this.rotation = Number(e.target.value);
			this.cropper.rotateTo(this.rotation);
		},
		rotateBy(deg) {
			if (!this.cropper) return;
			this.rotation = ((((this.rotation + deg + 180) % 360) + 360) % 360) - 180; // wrap to -180..180
			this.cropper.rotateTo(this.rotation);
		},
		confirm() {
			if (!this.cropper) return;
			const canvas = this.cropper.getCroppedCanvas({
				maxWidth: 1600,
				maxHeight: 1600,
				fillColor: "#fff", // rotated corners / transparent PNGs become white
				imageSmoothingQuality: "high",
			});
			canvas.toBlob(
				(blob) => {
					if (!blob) return;
					const name = this.fileName.replace(/\.[^.]+$/, "") + ".jpg";
					this.$emit("done", new File([blob], name, { type: "image/jpeg" }));
				},
				"image/jpeg",
				0.9
			);
		},
	},
};
</script>